import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
    AlertCircle,
    CheckCircle2,
    Loader2,
    RotateCcw,
    Save,
    UploadCloud,
} from "lucide-react";
import { BrandButton } from "@/components/ui/brand-button";
import { cn } from "@/lib/utils";
import { extractErrorMessage } from "@/lib/admin-utils";
import { invalidatePageContentCache } from "@/hooks/usePageContentOverrides";
import { SERVICES_DATA } from "@/data/services";
import { MASONRY_SERVICE } from "@/data/masonry-service";
import { COMMERCIAL_SERVICES_DATA } from "@/data/commercial-services";
import { INDUSTRIAL_SERVICES_DATA } from "@/data/industrial-services";
import { COMMERCIAL_HOME, INDUSTRIAL_HOME } from "@/data/track-content";
import { PROJECT_PHOTOS } from "@/data/photos";
import { TRACKS } from "@/lib/tracks";
import { PAGE_META } from "@/lib/seo-constants";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/page-content`;
const MEDIA_UPLOAD = `${BACKEND_URL}/api/media/upload`;

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

const SERVICE_DATA_BY_TRACK = {
    residential: { ...SERVICES_DATA, masonry: MASONRY_SERVICE },
    commercial: COMMERCIAL_SERVICES_DATA,
    industrial: INDUSTRIAL_SERVICES_DATA,
};
const TRACK_HOME_CONTENT = { commercial: COMMERCIAL_HOME, industrial: INDUSTRIAL_HOME };
const RESIDENTIAL_HOME_HERO =
    PROJECT_PHOTOS.interlocking?.[0]?.src ||
    PROJECT_PHOTOS.fencing?.[0]?.src ||
    "/projects/fencing/aluminum-fence-img-20250502-wa0056.webp";

const GROUP_ORDER = [
    "Residential services",
    "Commercial services",
    "Industrial services",
    "Home pages",
    "Splash cards",
];

const buildKeys = () => {
    const out = [];
    Object.entries(SERVICE_DATA_BY_TRACK).forEach(([track, data]) => {
        Object.values(data).forEach((s) => {
            out.push({
                key: `service:${track}:${s.slug}`,
                label: `${cap(track)} service · ${s.name}`,
                group: `${cap(track)} services`,
            });
        });
    });
    ["residential", "commercial", "industrial"].forEach((t) =>
        out.push({ key: `track-home:${t}`, label: `${cap(t)} home page`, group: "Home pages" }),
    );
    ["residential", "commercial", "industrial"].forEach((t) =>
        out.push({ key: `splash-card:${t}`, label: `Splash card · ${cap(t)}`, group: "Splash cards" }),
    );
    return out;
};

const resolveDefault = (pageKey) => {
    const [kind, a, b] = pageKey.split(":");
    if (kind === "service") {
        const data = SERVICE_DATA_BY_TRACK[a]?.[b];
        if (!data) return null;
        const trackLabel = a === "residential" ? "" : `${cap(a)} `;
        const pm = PAGE_META[b];
        return {
            kind,
            track: a,
            hasSeo: true,
            hasMaterials: true,
            hasServiceSections: false,
            hero_image: data.heroImage,
            hero_image_alt: `${data.name} project by Reformity Inc.`,
            seo_title: pm?.title || `${data.name} — ${trackLabel}Contractors GTA | Reformity Inc.`,
            seo_description: pm?.description || data.heroSubhead,
            materials: (data.materials || []).map((m) => ({ name: m.name, image: m.image })),
        };
    }
    if (kind === "track-home") {
        if (a === "residential") {
            const pm = PAGE_META.home;
            return {
                kind,
                track: a,
                hasSeo: true,
                hasMaterials: false,
                hasServiceSections: false,
                hero_image: RESIDENTIAL_HOME_HERO,
                hero_image_alt: "Modern Brampton home exterior at twilight — built by Reformity Inc.",
                seo_title: pm?.title || "",
                seo_description: pm?.description || "",
                materials: [],
            };
        }
        const content = TRACK_HOME_CONTENT[a];
        const pm = PAGE_META[`${a}Home`];
        return {
            kind,
            track: a,
            hasSeo: true,
            hasMaterials: false,
            hasServiceSections: true, // commercial/industrial homes show a grid of service tiles
            hero_image: content?.hero?.image,
            hero_image_alt: `${TRACKS[a]?.label} contracting by Reformity Inc. across the GTA`,
            seo_title: pm?.title || "",
            seo_description: pm?.description || "",
            materials: [],
        };
    }
    if (kind === "splash-card") {
        return {
            kind,
            track: a,
            hasSeo: false,
            hasMaterials: false,
            hasServiceSections: false,
            hero_image: TRACKS[a]?.image,
            hero_image_alt: `${TRACKS[a]?.label} contracting — ${TRACKS[a]?.cardTitle}`,
            materials: [],
        };
    }
    return null;
};

const Badge = ({ overridden }) => (
    <span
        className={cn(
            "inline-block text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5",
            overridden ? "bg-brand-cyan/15 text-brand-cyan" : "bg-brand-gray/20 text-brand-dark/60",
        )}
    >
        {overridden ? "Overridden" : "Default"}
    </span>
);

const inputCls =
    "block w-full bg-white border border-brand-dark/15 px-3 py-2 text-sm text-brand-dark placeholder:text-brand-gray focus:border-brand-cyan focus:outline-none transition-colors";

const FieldLabel = ({ children, overridden }) => (
    <span className="flex items-center gap-2 text-[10px] uppercase tracking-wider font-semibold text-brand-gray mb-1">
        {children}
        {overridden !== undefined && <Badge overridden={overridden} />}
    </span>
);

// URL field + inline Upload button (uploads straight to the Media Library and
// fills the URL). Also shows a small thumbnail of the current value.
const ImageInput = ({ value, onChange, placeholder, token, testid }) => {
    const [uploading, setUploading] = useState(false);
    const [err, setErr] = useState("");
    const fileRef = useRef(null);

    const onFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        setErr("");
        try {
            const fd = new FormData();
            fd.append("file", file);
            const res = await axios.post(MEDIA_UPLOAD, fd, { headers: { "X-Admin-Token": token } });
            onChange(res.data.url);
        } catch (e2) {
            setErr(extractErrorMessage(e2, "Upload failed."));
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const preview = (value || "").trim() || placeholder;

    return (
        <div>
            <div className="flex items-start gap-2">
                {preview && (
                    <img
                        src={preview}
                        alt=""
                        className="h-10 w-14 shrink-0 object-cover border border-brand-dark/10 bg-brand-offwhite"
                    />
                )}
                <input
                    className={inputCls}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                    data-testid={testid}
                />
                <input
                    type="file"
                    accept="image/*,video/*"
                    ref={fileRef}
                    onChange={onFile}
                    className="hidden"
                />
                <BrandButton
                    type="button"
                    variant="outline"
                    tone="onLight"
                    size="md"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    className="shrink-0"
                    data-testid={testid ? `${testid}-upload` : undefined}
                >
                    {uploading ? (
                        <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2.25} />
                    ) : (
                        <UploadCloud className="h-4 w-4" strokeWidth={2.25} />
                    )}
                    Upload
                </BrandButton>
            </div>
            {err && (
                <p className="text-xs text-brand-coral mt-1 flex items-start gap-1">
                    <AlertCircle className="h-3.5 w-3.5 mt-0.5" strokeWidth={2.5} />
                    {err}
                </p>
            )}
        </div>
    );
};

const AdminPageContentPanel = ({ token }) => {
    const keys = useMemo(buildKeys, []);
    const [pageKey, setPageKey] = useState(keys[0]?.key || "");
    const [overrideMap, setOverrideMap] = useState({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [ok, setOk] = useState("");
    const [form, setForm] = useState({
        hero_image: "",
        hero_image_alt: "",
        seo_title: "",
        seo_description: "",
        materials: {},
    });
    const [serviceImages, setServiceImages] = useState({});

    const headers = useMemo(() => ({ "X-Admin-Token": token }), [token]);
    const def = useMemo(() => resolveDefault(pageKey), [pageKey]);
    const override = overrideMap[pageKey];

    const loadAll = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const res = await axios.get(API);
            const map = {};
            (Array.isArray(res.data) ? res.data : []).forEach((o) => {
                if (o && o.pageKey) map[o.pageKey] = o;
            });
            setOverrideMap(map);
        } catch (e) {
            setError(extractErrorMessage(e, "Could not load overrides."));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadAll();
    }, [loadAll]);

    // Reset the form whenever the selected page or the loaded overrides change.
    useEffect(() => {
        const ov = overrideMap[pageKey];
        setForm({
            hero_image: ov?.hero_image || "",
            hero_image_alt: ov?.hero_image_alt || "",
            seo_title: ov?.seo_title || "",
            seo_description: ov?.seo_description || "",
            materials: Object.fromEntries((ov?.materials || []).map((m) => [m.name, m.image])),
        });
        if (def?.hasServiceSections) {
            const svc = {};
            Object.values(SERVICE_DATA_BY_TRACK[def.track]).forEach((s) => {
                svc[s.slug] = overrideMap[`service:${def.track}:${s.slug}`]?.hero_image || "";
            });
            setServiceImages(svc);
        } else {
            setServiceImages({});
        }
        setOk("");
        setError("");
    }, [pageKey, overrideMap, def]);

    const set = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));
    const setHero = (val) => setForm((prev) => ({ ...prev, hero_image: val }));
    const setMaterial = (name, value) =>
        setForm((prev) => ({ ...prev, materials: { ...prev.materials, [name]: value } }));
    const setServiceImage = (slug, value) =>
        setServiceImages((prev) => ({ ...prev, [slug]: value }));

    const save = async () => {
        setSaving(true);
        setError("");
        setOk("");
        try {
            const payload = {
                hero_image: form.hero_image.trim() || null,
                hero_image_alt: form.hero_image_alt.trim() || null,
                seo_title: form.seo_title.trim() || null,
                seo_description: form.seo_description.trim() || null,
            };
            if (def?.hasMaterials) {
                payload.materials = Object.entries(form.materials)
                    .filter(([, img]) => img && img.trim())
                    .map(([name, image]) => ({ name, image: image.trim() }));
            }
            await axios.put(`${API}/${encodeURIComponent(pageKey)}`, payload, { headers });

            // Track-home service tiles → write each changed one to its own service
            // override, preserving that service's other override fields.
            if (def?.hasServiceSections) {
                for (const [slug, img] of Object.entries(serviceImages)) {
                    const key = `service:${def.track}:${slug}`;
                    const baseline = overrideMap[key]?.hero_image || "";
                    if ((img || "").trim() === baseline) continue;
                    const existing = overrideMap[key] || {};
                    await axios.put(
                        `${API}/${encodeURIComponent(key)}`,
                        {
                            hero_image: (img || "").trim() || null,
                            hero_image_alt: existing.hero_image_alt || null,
                            seo_title: existing.seo_title || null,
                            seo_description: existing.seo_description || null,
                            materials: existing.materials || null,
                        },
                        { headers },
                    );
                }
            }

            invalidatePageContentCache();
            await loadAll();
            setOk("Saved. Reload a public page to see the change.");
        } catch (e) {
            setError(extractErrorMessage(e, "Save failed."));
        } finally {
            setSaving(false);
        }
    };

    // On track-home pages the service tiles are stored under their own service:*
    // keys, so a reset must clear those too — and the button must be enabled if
    // only tiles were overridden.
    const relatedServiceKeys = def?.hasServiceSections
        ? Object.values(SERVICE_DATA_BY_TRACK[def.track]).map(
              (s) => `service:${def.track}:${s.slug}`,
          )
        : [];
    const hasAnyOverride = !!override || relatedServiceKeys.some((k) => overrideMap[k]);

    const reset = async () => {
        const msg = def?.hasServiceSections
            ? "Reset this home page — hero, SEO, and all of its service tile images — to defaults?"
            : "Reset this page to its built-in default content?";
        if (!window.confirm(msg)) return;
        setSaving(true);
        setError("");
        setOk("");
        try {
            await axios.delete(`${API}/${encodeURIComponent(pageKey)}`, { headers });
            for (const k of relatedServiceKeys) {
                if (overrideMap[k]) {
                    await axios.delete(`${API}/${encodeURIComponent(k)}`, { headers });
                }
            }
            invalidatePageContentCache();
            await loadAll();
            setOk("Reset to default.");
        } catch (e) {
            setError(extractErrorMessage(e, "Reset failed."));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div data-testid="admin-page-content-panel">
            <div className="mb-6">
                <h2 className="font-display text-3xl tracking-wide text-brand-dark leading-none">
                    Page Content &amp; SEO
                </h2>
                <p className="mt-2 text-sm text-brand-dark/70">
                    Override any page's images, alt text, and SEO without a code deploy. Upload a
                    photo right here or paste a URL. Leave a field blank to keep the built-in default.
                </p>
            </div>

            <label className="block mb-6 max-w-lg">
                <span className="block text-[10px] uppercase tracking-wider font-semibold text-brand-gray mb-1">
                    Page
                </span>
                <select
                    className={inputCls}
                    value={pageKey}
                    onChange={(e) => setPageKey(e.target.value)}
                    data-testid="admin-page-content-select"
                >
                    {GROUP_ORDER.map((group) => (
                        <optgroup key={group} label={group}>
                            {keys
                                .filter((k) => k.group === group)
                                .map((k) => (
                                    <option key={k.key} value={k.key}>
                                        {k.label}
                                    </option>
                                ))}
                        </optgroup>
                    ))}
                </select>
            </label>

            {loading || !def ? (
                <div className="flex items-center justify-center py-16">
                    <Loader2 className="h-8 w-8 text-brand-cyan animate-spin" strokeWidth={2} />
                </div>
            ) : (
                <div className="bg-white border border-brand-dark/10 p-6 md:p-8 max-w-3xl" data-testid="admin-page-content-editor">
                    <label className="block mb-5">
                        <FieldLabel overridden={!!override?.hero_image}>Hero image</FieldLabel>
                        <ImageInput
                            value={form.hero_image}
                            onChange={setHero}
                            placeholder={def.hero_image || "/media/images/..."}
                            token={token}
                            testid="admin-page-content-hero-image"
                        />
                    </label>

                    <label className="block mb-5">
                        <FieldLabel overridden={!!override?.hero_image_alt}>Hero image alt text</FieldLabel>
                        <input
                            className={inputCls}
                            value={form.hero_image_alt}
                            onChange={set("hero_image_alt")}
                            placeholder={def.hero_image_alt || ""}
                            data-testid="admin-page-content-hero-alt"
                        />
                    </label>

                    {def.hasSeo && (
                        <>
                            <label className="block mb-5">
                                <FieldLabel overridden={!!override?.seo_title}>SEO title</FieldLabel>
                                <input
                                    className={inputCls}
                                    value={form.seo_title}
                                    onChange={set("seo_title")}
                                    placeholder={def.seo_title || ""}
                                    maxLength={120}
                                    data-testid="admin-page-content-seo-title"
                                />
                            </label>
                            <label className="block mb-5">
                                <FieldLabel overridden={!!override?.seo_description}>SEO description</FieldLabel>
                                <textarea
                                    className={cn(inputCls, "min-h-[80px]")}
                                    value={form.seo_description}
                                    onChange={set("seo_description")}
                                    placeholder={def.seo_description || ""}
                                    maxLength={320}
                                    data-testid="admin-page-content-seo-desc"
                                />
                            </label>
                        </>
                    )}

                    {def.hasMaterials && def.materials.length > 0 && (
                        <div className="mb-5">
                            <span className="block text-[10px] uppercase tracking-wider font-semibold text-brand-gray mb-2">
                                Section / material images
                            </span>
                            <div className="space-y-4">
                                {def.materials.map((m) => (
                                    <div key={m.name}>
                                        <FieldLabel
                                            overridden={!!override?.materials?.find((om) => om.name === m.name)?.image}
                                        >
                                            {m.name}
                                        </FieldLabel>
                                        <ImageInput
                                            value={form.materials[m.name] || ""}
                                            onChange={(v) => setMaterial(m.name, v)}
                                            placeholder={m.image || "/media/images/..."}
                                            token={token}
                                            testid={`admin-page-content-material-${m.name.toLowerCase().replace(/\s+/g, "-")}`}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {def.hasServiceSections && (
                        <div className="mb-5 pt-4 border-t border-brand-dark/10">
                            <span className="block text-[10px] uppercase tracking-wider font-semibold text-brand-gray mb-1">
                                Service tile images (the grid on this home page)
                            </span>
                            <p className="text-[11px] text-brand-gray mb-3">
                                Each tile is shared with that service's own page.
                            </p>
                            <div className="space-y-4">
                                {Object.values(SERVICE_DATA_BY_TRACK[def.track]).map((s) => (
                                    <div key={s.slug}>
                                        <FieldLabel
                                            overridden={!!overrideMap[`service:${def.track}:${s.slug}`]?.hero_image}
                                        >
                                            {s.name}
                                        </FieldLabel>
                                        <ImageInput
                                            value={serviceImages[s.slug] || ""}
                                            onChange={(v) => setServiceImage(s.slug, v)}
                                            placeholder={s.heroImage || "/media/images/..."}
                                            token={token}
                                            testid={`admin-page-content-service-${s.slug}`}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="flex items-center gap-3 flex-wrap pt-4 border-t border-brand-dark/10 mt-2">
                        <BrandButton
                            type="button"
                            variant="primary"
                            size="md"
                            onClick={save}
                            disabled={saving}
                            data-testid="admin-page-content-save"
                        >
                            {saving ? (
                                <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2.25} />
                            ) : (
                                <Save className="h-4 w-4" strokeWidth={2.25} />
                            )}
                            Save
                        </BrandButton>
                        <button
                            type="button"
                            onClick={reset}
                            disabled={saving || !hasAnyOverride}
                            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider font-semibold text-brand-coral hover:text-brand-dark transition-colors disabled:opacity-40"
                            data-testid="admin-page-content-reset"
                        >
                            <RotateCcw className="h-3.5 w-3.5" strokeWidth={2.5} />
                            Reset this page to default
                        </button>
                    </div>

                    {ok && (
                        <p className="mt-3 flex items-center gap-1.5 text-xs text-brand-cyan" data-testid="admin-page-content-ok">
                            <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={2.5} />
                            {ok}
                        </p>
                    )}
                    {error && (
                        <p className="mt-3 flex items-start gap-1.5 text-xs text-brand-coral" data-testid="admin-page-content-error">
                            <AlertCircle className="h-3.5 w-3.5 mt-0.5" strokeWidth={2.5} />
                            {error}
                        </p>
                    )}
                </div>
            )}
        </div>
    );
};

export default AdminPageContentPanel;
